import { useReducedMotion } from '../../hooks/useReducedMotion'
import { SubjectAnimation, SubjectSquareLayers } from '../SubjectAnimation'

interface SubjectLoadingScreenProps {
  /** Subject slug whose square animation plays. Falls back to an empty square
   *  until (or unless) the animation loads. */
  subjectSlug?: string
  /** Accessible status label, and the visible caption when `showCaption`. */
  label: string
  /** Show `label` as a caption beneath the square. Defaults to true. */
  showCaption?: boolean
}

// Shared loading visual: the subject's square animation centered in its
// container, with an optional caption beneath. Nothing is drawn in the square
// until the animation is ready (no placeholder flash — `restingMark={null}`),
// except under reduced motion, where the animation never plays and the static
// square is the only visible loading cue.
// Used by the lesson loading overlay (ChatLoadingScreen) and the Subject modal,
// replacing the generic spinner so both show the subject's own square.
export const SubjectLoadingScreen = ({
  subjectSlug,
  label,
  showCaption = true,
}: SubjectLoadingScreenProps) => {
  const prefersReducedMotion = useReducedMotion()

  return (
    <div
      role='status'
      aria-label={label}
      className='flex h-full w-full flex-col items-center justify-center gap-6'
    >
      <div className='w-22'>
        <SubjectAnimation
          slug={subjectSlug}
          isActive
          restingMark={prefersReducedMotion ? <SubjectSquareLayers /> : null}
        />
      </div>
      {showCaption && (
        <p className='text-body-3 font-semibold leading-body-3 text-edu-fg-primary'>
          {label}
        </p>
      )}
    </div>
  )
}
